import { useEffect, useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Lock, Save } from 'lucide-react';
import { AadhaarInput } from '@/components/ui/AadhaarInput';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { FormSection } from '@/components/ui/FormSection';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { BLOOD_GROUP_OPTIONS, GENDER_OPTIONS } from '@/constants/institute';
import { MARITAL_STATUS_OPTIONS } from '@/features/institute/staff/constants';
import { NOT_SET_OPTION } from '@/features/institute/staff/components/StaffDetailsFields';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';
import { ApiRequestError } from '@/lib/apiClient';
import { applyFieldErrors } from '@/lib/forms';
import type { OwnStaffProfile } from '@/types/staff';
import { teacherApi } from '../../api/teacher.api';
import {
  BANK_FIELDS,
  ownProfileSchema,
  type OwnProfileFormValues,
  type OwnProfilePayload,
} from '../../schemas/ownProfile.schema';

export interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: OwnStaffProfile;
  onSaved: (profile: OwnStaffProfile) => void;
}

/** The record as the form holds it — every field a string. */
function toFormValues(profile: OwnStaffProfile): OwnProfileFormValues {
  const d = profile.details;
  const text = (value: string | null | undefined) => value ?? '';
  return {
    mobile: text(profile.mobile),
    currentPassword: '',
    details: {
      dateOfBirth: text(d?.dateOfBirth),
      gender: text(d?.gender) as NonNullable<OwnProfileFormValues['details']>['gender'],
      bloodGroup: text(d?.bloodGroup) as NonNullable<OwnProfileFormValues['details']>['bloodGroup'],
      maritalStatus: text(d?.maritalStatus) as NonNullable<
        OwnProfileFormValues['details']
      >['maritalStatus'],
      nationality: text(d?.nationality),
      aadhaarNumber: text(d?.aadhaarNumber),
      panNumber: text(d?.panNumber),
      uanNumber: text(d?.uanNumber),
      passportNumber: text(d?.passportNumber),
      alternateMobile: text(d?.alternateMobile),
      emergencyContactName: text(d?.emergencyContactName),
      emergencyContactMobile: text(d?.emergencyContactMobile),
      presentAddress: text(d?.presentAddress),
      permanentAddress: text(d?.permanentAddress),
      bankName: text(d?.bankName),
      bankAccountNumber: text(d?.bankAccountNumber),
      bankIfsc: text(d?.bankIfsc),
      bankAccountHolder: text(d?.bankAccountHolder),
      qualification: text(d?.qualification),
      languagesKnown: text(d?.languagesKnown),
      experienceYears: d?.experienceYears == null ? '' : String(d.experienceYears),
      skills: text(d?.skills),
    },
  };
}

/**
 * A member of staff keeping their own record up to date.
 *
 * Everything that is theirs: personal details, phones and addresses, identity
 * numbers, the bank account salary is paid into, and their background. The
 * account and the employment terms stay with the school office, which is who
 * answers for them, so they are not on this form.
 *
 * The password box appears only when the mobile number or the bank account has
 * changed — the two edits that, from a session left open, would take over an
 * account or a salary. Every field is sent, and a blank one is cleared.
 */
export function EditProfileModal({ isOpen, onClose, profile, onSaved }: EditProfileModalProps) {
  const formId = useId();
  const [formError, setFormError] = useState<string | null>(null);
  const [original, setOriginal] = useState<OwnProfileFormValues>(() => toFormValues(profile));

  // `raw` keeps the form holding what the inputs hold — strings — and the submit
  // handler parses them into the payload, as the office's staff form does.
  const form = useForm<OwnProfileFormValues>({
    resolver: zodResolver(ownProfileSchema, undefined, { raw: true }),
    defaultValues: original,
  });
  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = form;
  useLiveFormErrors(form);

  useEffect(() => {
    if (!isOpen) return;
    const values = toFormValues(profile);
    setOriginal(values);
    reset(values);
    setFormError(null);
  }, [isOpen, profile, reset]);

  const mobileChanged = (watch('mobile') ?? '').trim() !== original.mobile;
  const bankChanged = BANK_FIELDS.some(
    (field) => (watch(`details.${field}`) ?? '').trim() !== (original.details?.[field] ?? ''),
  );
  const needsPassword = mobileChanged || bankChanged;
  const fieldErrors = errors.details;

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      const payload: OwnProfilePayload = ownProfileSchema.parse(values);
      const saved = await teacherApi.updateMyProfile({
        ...payload,
        // Only ever sent when it is needed, so it is never on the wire for nothing.
        currentPassword: needsPassword ? payload.currentPassword : undefined,
      });
      onSaved(saved);
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        applyFieldErrors(setError, caught);
        setFormError(caught.message);
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit my profile"
      description="Keep your details up to date. Your role, employee ID, joining date and employment terms are kept by the school office."
      size="xl"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            isLoading={isSubmitting}
            leftIcon={<Save className="h-4 w-4" />}
          >
            Save
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
        {formError && (
          <Alert tone="critical" title="Could not save your profile">
            {formError}
          </Alert>
        )}

        <FormSection title="Personal" columns={2}>
          <DatePicker
            label="Date of birth"
            error={fieldErrors?.dateOfBirth?.message}
            {...register('details.dateOfBirth')}
          />
          <Select
            label="Gender"
            options={[NOT_SET_OPTION, ...GENDER_OPTIONS]}
            error={fieldErrors?.gender?.message}
            {...register('details.gender')}
          />
          <Select
            label="Blood group"
            options={[NOT_SET_OPTION, ...BLOOD_GROUP_OPTIONS]}
            error={fieldErrors?.bloodGroup?.message}
            {...register('details.bloodGroup')}
          />
          <Select
            label="Marital status"
            options={[NOT_SET_OPTION, ...MARITAL_STATUS_OPTIONS]}
            error={fieldErrors?.maritalStatus?.message}
            {...register('details.maritalStatus')}
          />
          <Input
            label="Nationality"
            placeholder="Indian"
            error={fieldErrors?.nationality?.message}
            {...register('details.nationality')}
          />
        </FormSection>

        <FormSection title="Contact & address" columns={2}>
          <PhoneInput
            label="Mobile number"
            autoComplete="tel-national"
            hint="You can sign in with this number."
            error={errors.mobile?.message}
            {...register('mobile')}
          />
          <PhoneInput
            label="Alternate number"
            autoComplete="off"
            error={fieldErrors?.alternateMobile?.message}
            {...register('details.alternateMobile')}
          />
          <Input
            label="Emergency contact name"
            error={fieldErrors?.emergencyContactName?.message}
            {...register('details.emergencyContactName')}
          />
          <PhoneInput
            label="Emergency contact number"
            autoComplete="off"
            error={fieldErrors?.emergencyContactMobile?.message}
            {...register('details.emergencyContactMobile')}
          />
          <Textarea
            label="Present address"
            rows={3}
            placeholder="House number, street, area, city, PIN code"
            error={fieldErrors?.presentAddress?.message}
            {...register('details.presentAddress')}
          />
          <Textarea
            label="Permanent address"
            rows={3}
            placeholder="Leave blank if it is the same"
            error={fieldErrors?.permanentAddress?.message}
            {...register('details.permanentAddress')}
          />
        </FormSection>

        <FormSection
          title="Identity"
          description="Stored encrypted. Only you and the staff who manage records see them in full."
          columns={2}
        >
          <AadhaarInput
            label="Aadhaar number"
            error={fieldErrors?.aadhaarNumber?.message}
            {...register('details.aadhaarNumber')}
          />
          <Input
            label="PAN"
            placeholder="ABCDE1234F"
            className="uppercase"
            error={fieldErrors?.panNumber?.message}
            {...register('details.panNumber')}
          />
          <Input
            label="UAN"
            inputMode="numeric"
            placeholder="12 digits"
            error={fieldErrors?.uanNumber?.message}
            {...register('details.uanNumber')}
          />
          <Input
            label="Passport number"
            className="uppercase"
            error={fieldErrors?.passportNumber?.message}
            {...register('details.passportNumber')}
          />
        </FormSection>

        <FormSection
          title="Bank account"
          description="Where your salary is paid. Changing it asks for your password."
          columns={2}
        >
          <Input
            label="Bank name"
            error={fieldErrors?.bankName?.message}
            {...register('details.bankName')}
          />
          <Input
            label="Account holder name"
            error={fieldErrors?.bankAccountHolder?.message}
            {...register('details.bankAccountHolder')}
          />
          <Input
            label="Account number"
            inputMode="numeric"
            autoComplete="off"
            error={fieldErrors?.bankAccountNumber?.message}
            {...register('details.bankAccountNumber')}
          />
          <Input
            label="IFSC"
            placeholder="SBIN0001234"
            className="uppercase"
            error={fieldErrors?.bankIfsc?.message}
            {...register('details.bankIfsc')}
          />
        </FormSection>

        <FormSection title="Background" columns={2}>
          <Input
            label="Qualification"
            placeholder="M.Sc, B.Ed"
            error={fieldErrors?.qualification?.message}
            {...register('details.qualification')}
          />
          <Input
            label="Experience (years)"
            inputMode="decimal"
            placeholder="5"
            error={fieldErrors?.experienceYears?.message}
            {...register('details.experienceYears')}
          />
          <Input
            label="Languages known"
            placeholder="Telugu, English, Hindi"
            error={fieldErrors?.languagesKnown?.message}
            {...register('details.languagesKnown')}
          />
          <Input
            label="Skills & interests"
            error={fieldErrors?.skills?.message}
            {...register('details.skills')}
          />
        </FormSection>

        {needsPassword && (
          <Input
            label="Current password"
            isPassword
            autoComplete="current-password"
            leftIcon={<Lock className="h-4 w-4" />}
            hint={
              mobileChanged && bankChanged
                ? 'Needed to change your mobile number and bank account.'
                : mobileChanged
                  ? 'Needed to change the number you sign in with.'
                  : 'Needed to change the account your salary is paid into.'
            }
            error={errors.currentPassword?.message}
            {...register('currentPassword')}
          />
        )}
      </form>
    </Modal>
  );
}
